import { useState, useEffect, useCallback } from 'react';
import {
  getAffaireSettings,
  getResponsableHistory,
  getEligibleResponsables,
  changePrimaryResponsable,
} from '../api/affaireSettings.api';
import type {
  AffaireSettings,
  ResponsableHistoryEntry,
  EligibleResponsable,
  ChangeResponsableRequest,
} from '../types/tracker.types';

/**
 * Hook to fetch affaire settings including responsables
 */
export const useAffaireSettings = (affaireId: number | null) => {
  const [settings, setSettings] = useState<AffaireSettings | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = useCallback(async () => {
    if (!affaireId) {
      setSettings(null);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const response = await getAffaireSettings(affaireId);
      if (response.success) {
        setSettings(response.data);
      } else {
        setError('Failed to fetch affaire settings');
      }
    } catch (err) {
      console.error('Error fetching affaire settings:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setIsLoading(false);
    }
  }, [affaireId]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  return {
    settings,
    isLoading,
    error,
    refetch: fetchSettings,
  };
};

/**
 * Hook to fetch responsable change history
 */
export const useResponsableHistory = (affaireId: number | null) => {
  const [history, setHistory] = useState<ResponsableHistoryEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchHistory = useCallback(async () => {
    if (!affaireId) {
      setHistory([]);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const response = await getResponsableHistory(affaireId);
      if (response.success) {
        setHistory(response.data);
      } else {
        setError('Failed to fetch responsable history');
      }
    } catch (err) {
      console.error('Error fetching responsable history:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setIsLoading(false);
    }
  }, [affaireId]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  return {
    history,
    isLoading,
    error,
    refetch: fetchHistory,
  };
};

/**
 * Hook to fetch eligible responsables (bureau/admin users)
 */
export const useEligibleResponsables = () => {
  const [eligibleUsers, setEligibleUsers] = useState<EligibleResponsable[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchEligibleUsers = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getEligibleResponsables();
      if (response.success) {
        setEligibleUsers(response.data);
      } else {
        setError('Failed to fetch eligible responsables');
      }
    } catch (err) {
      console.error('Error fetching eligible responsables:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEligibleUsers();
  }, [fetchEligibleUsers]);

  return {
    eligibleUsers,
    isLoading,
    error,
    refetch: fetchEligibleUsers,
  };
};

/**
 * Hook to manage changing primary responsable
 */
export const useChangeResponsable = () => {
  const [isChanging, setIsChanging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const changeResponsable = async (
    affaireId: number,
    request: ChangeResponsableRequest
  ): Promise<boolean> => {
    try {
      setIsChanging(true);
      setError(null);
      const response = await changePrimaryResponsable(affaireId, request);

      if (response.success) {
        return response.changed;
      } else {
        setError(response.message || 'Failed to change responsable');
        return false;
      }
    } catch (err) {
      console.error('Error changing responsable:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      return false;
    } finally {
      setIsChanging(false);
    }
  };

  return {
    changeResponsable,
    isChanging,
    error,
  };
};
